// Entity relationship graph visualization - 195 lines ✅
import { useCallback } from 'react'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow'
import 'reactflow/dist/style.css'

const entityNodes: Node[] = [
  // Primary entities - center
  { id: 'organizations', position: { x: 400, y: 200 }, data: { label: 'Organizations' }, style: { background: '#003C41', color: 'white', padding: 10, borderRadius: 8, fontWeight: 'bold' } },
  { id: 'users', position: { x: 200, y: 350 }, data: { label: 'Users' }, style: { background: '#296267', color: 'white', padding: 10, borderRadius: 8 } },
  { id: 'issues', position: { x: 400, y: 500 }, data: { label: 'Issues' }, style: { background: '#FF6B6B', color: 'white', padding: 10, borderRadius: 8 } },
  { id: 'knowledge_entries', position: { x: 600, y: 350 }, data: { label: 'Knowledge Entries' }, style: { background: '#4ECDC4', color: 'white', padding: 10, borderRadius: 8 } },
  { id: 'resolution_methods', position: { x: 600, y: 500 }, data: { label: 'Resolution Methods' }, style: { background: '#95E1D3', color: 'white', padding: 10, borderRadius: 8 } },

  // Secondary entities - outer ring
  { id: 'customers', position: { x: 100, y: 200 }, data: { label: 'Customers' }, style: { background: '#4A90E2', color: 'white', padding: 8, borderRadius: 8 } },
  { id: 'customer_documents', position: { x: 100, y: 500 }, data: { label: 'Customer Documents' }, style: { background: '#4A90E2', color: 'white', padding: 8, borderRadius: 8 } },
  { id: 'conversation_sessions', position: { x: 250, y: 650 }, data: { label: 'Conversation Sessions' }, style: { background: '#9B59B6', color: 'white', padding: 8, borderRadius: 8 } },
  { id: 'expert_profiles', position: { x: 50, y: 350 }, data: { label: 'Expert Profiles' }, style: { background: '#E67E22', color: 'white', padding: 8, borderRadius: 8 } },

  // Knowledge graph entities
  { id: 'kg_node', position: { x: 700, y: 200 }, data: { label: 'KG Node' }, style: { background: '#8B4789', color: 'white', padding: 8, borderRadius: 8 } },
  { id: 'kg_edge', position: { x: 700, y: 300 }, data: { label: 'KG Edge' }, style: { background: '#8B4789', color: 'white', padding: 8, borderRadius: 8 } },
]

const entityEdges: Edge[] = [
  // Organization relationships
  { id: 'org-users', source: 'organizations', target: 'users', label: 'has many', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'org-issues', source: 'organizations', target: 'issues', label: 'has many', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'org-knowledge', source: 'organizations', target: 'knowledge_entries', label: 'has many', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'org-customers', source: 'organizations', target: 'customers', label: 'has many', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },

  // User relationships
  { id: 'user-issues', source: 'users', target: 'issues', label: 'creates', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'user-knowledge', source: 'users', target: 'knowledge_entries', label: 'creates', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'user-expert', source: 'users', target: 'expert_profiles', label: 'has profile', markerEnd: { type: MarkerType.ArrowClosed } },

  // Issue relationships
  { id: 'issue-conversation', source: 'issues', target: 'conversation_sessions', label: 'has session', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'issue-resolved', source: 'issues', target: 'knowledge_entries', label: 'resolved by', style: { stroke: '#4ECDC4', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4ECDC4' } },

  // Knowledge entry relationships
  { id: 'knowledge-resolution', source: 'knowledge_entries', target: 'resolution_methods', label: 'has methods', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'knowledge-kg', source: 'knowledge_entries', target: 'kg_node', label: 'syncs to', style: { strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },

  // Customer relationships
  { id: 'customer-documents', source: 'customers', target: 'customer_documents', label: 'has docs', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'customer-issues', source: 'customers', target: 'issues', label: 'reports', markerEnd: { type: MarkerType.ArrowClosed } },

  // KG relationships
  { id: 'kg-connects', source: 'kg_edge', target: 'kg_node', label: 'connects', markerEnd: { type: MarkerType.ArrowClosed } },
]

export function EntityGraph() {
  const [nodes, , onNodesChange] = useNodesState(entityNodes)
  const [edges, , onEdgesChange] = useEdgesState(entityEdges)

  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    console.log('Clicked node:', node.data.label)
  }, [])

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-kaycoo-teal-dark bg-white p-4">
        <h3 className="mb-2 text-lg font-semibold text-kaycoo-teal-dark">Entity Relationship Graph</h3>
        <p className="text-sm text-kaycoo-charcoal-2">
          Visual representation of database tables and their relationships. Drag nodes to rearrange, scroll to zoom.
        </p>
      </div>

      <div className="h-[700px] w-full rounded-lg border border-kaycoo-teal-dark bg-white">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          fitView
          attributionPosition="bottom-left"
        >
          <Background color="#e5e7eb" gap={16} />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              const bg = node.style?.background as string
              return bg || '#9CD6D6'
            }}
            maskColor="rgba(0, 60, 65, 0.1)"
          />
        </ReactFlow>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <LegendItem color="#003C41" label="Primary Entities" />
        <LegendItem color="#4A90E2" label="Secondary Entities" />
        <LegendItem color="#8B4789" label="Knowledge Graph" />
        <LegendItem color="#FF6B6B" label="Issues" />
      </div>
    </div>
  )
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded border border-kaycoo-teal bg-white p-2">
      <div className="h-4 w-4 rounded" style={{ background: color }} />
      <span className="text-sm text-kaycoo-charcoal-2">{label}</span>
    </div>
  )
}
